import { Button, Collapse, DatePicker, Flex, Popover, Typography } from "antd";
import { useMediaQuery } from "@mui/material";
import { useState } from "react";
import dayjs from "dayjs";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { RecurringScheduleItems, ScheduleItemList } from "~/types";
import { useAppTranslation } from "~/hooks/useAppTranslation";

var minMax = require("dayjs/plugin/minMax");
dayjs.extend(minMax);
var weekday = require("dayjs/plugin/weekday");
dayjs.extend(weekday);

export const AmenitySchedule = ({
  scheduleItems,
  recurringScheduleItems,
  startWeek,
}: {
  scheduleItems?: ScheduleItemList[];
  recurringScheduleItems?: RecurringScheduleItems[];
  startWeek?: dayjs.Dayjs;
}) => {
  const { t } = useAppTranslation();
  const landscape = useMediaQuery("(orientation: landscape)");

  const daysShort = [
    t("SUN"),
    t("MON"),
    t("TUE"),
    t("WED"),
    t("THU"),
    t("FRI"),
    t("SAT"),
  ];
  const [schedWeek, setSchedWeek] = useState(startWeek ?? dayjs());
  const [enabledKeys, setEnabledKeys] = useState(
    scheduleItems?.map(() => true) ?? []
  );
  const [enabledRecurringKeys, setEnabledRecurringKeys] = useState(
    recurringScheduleItems?.map(() => true) ?? []
  );

  const toggleRecurringKey = (index: number) => {
    const copy = enabledRecurringKeys.concat();
    copy[index] = !copy[index];
    setEnabledRecurringKeys(copy);
  };

  const toggleKey = (index: number) => {
    const copy = enabledKeys.concat();
    copy[index] = !copy[index];
    setEnabledKeys(copy);
  };

  const displayTimes = [
    "12",
    "01",
    "02",
    "03",
    "04",
    "05",
    "06",
    "07",
    "08",
    "09",
    "10",
    "11",
  ];

  const allTimes = displayTimes
    .map((value) => value + "AM")
    .concat(displayTimes.map((value) => value + "PM"));

  const hourWidth = 61;
  const timeFrameTop = 34;
  const timeFrameLeft = 46;
  // width of each cell when table is vertical
  const vertCellWidth = 36;
  // height of each cell when the table is horizontal
  const horizCellHeight = 32;

  const getTimespanElement = (
    vertical: boolean,
    index: number,
    offset: number,
    size: number,
    color: string,
    onClick?: React.MouseEventHandler<HTMLDivElement>
  ) => (
    <div
      style={{
        left: vertical
          ? timeFrameLeft + index * vertCellWidth
          : hourWidth + offset,
        top: vertical ? timeFrameTop + offset : (1 + index) * horizCellHeight,
        width: vertical ? vertCellWidth : size,
        height: vertical ? size : horizCellHeight,
        background: color,
      }}
      className={"amenity-schedule-item"}
      onClick={onClick}
    />
  );

  const getTable = (vertical: boolean) => (
    <>
      <div
        className={"amenity-schedule-table " + (vertical ? "vert" : "horiz")}
      >
        <div className="amenity-schedule-row">
          <div className="amenity-schedule-cell amenity-schedule-corner" />
          {(vertical ? daysShort : allTimes).map((value) => (
            <div className="amenity-schedule-cell amenity-schedule-col-header">
              {value}
            </div>
          ))}
        </div>
        {(vertical ? allTimes : daysShort).map((value) => (
          <div className="amenity-schedule-row">
            <div className="amenity-schedule-cell amenity-schedule-row-header">
              {value}
            </div>
            {(vertical ? daysShort : allTimes).map(() => (
              <div className="amenity-schedule-cell"></div>
            ))}
          </div>
        ))}
      </div>
      {recurringScheduleItems
        ?.filter((_, index) => enabledRecurringKeys[index])
        .map((schedItem) =>
          schedItem.weeklyTimes.map((value, index) => {
            return value.map((timeFrame) => {
              const startH = timeFrame.start.hour();
              const startM = timeFrame.start.minute();
              const endH = timeFrame.end.hour();
              const endM = timeFrame.end.minute();

              const size = (endH - startH) * hourWidth + endM - startM;
              const offset = startH * hourWidth + startM;
              return getTimespanElement(
                vertical,
                index,
                offset,
                size,
                schedItem.color
              );
            });
          })
        )}
      {scheduleItems
        ?.filter((_, index) => enabledKeys[index])
        .map(({ scheduleItems, color }) =>
          scheduleItems.map((value) => {
            const timeFrame = value.timeFrame;
            const firstDay = schedWeek.startOf("week");
            const lastDay = schedWeek.endOf("week");
            if (
              !(
                timeFrame.start.isBefore(lastDay) &&
                timeFrame.end.isAfter(firstDay)
              )
            )
              return undefined;
            const elements = [];
            // @ts-ignore
            const start = dayjs.max(firstDay, timeFrame.start);
            // @ts-ignore
            const end = dayjs.min(lastDay, timeFrame.end);

            const startIndex = start.weekday();
            const days = end.day() - start.day();

            let startH = start.hour();
            let startM = start.minute();

            for (let i = days; i >= 0; i--) {
              const endH = i === 0 ? end.hour() : 23;
              const endM = i === 0 ? end.minute() : 59;

              const size = (endH - startH) * hourWidth + endM - startM;
              const index = startIndex + days - i;
              const offset = startH * hourWidth + startM;

              const timespan = getTimespanElement(
                vertical,
                index,
                offset,
                size,
                color
              );

              if (value.popOver) {
                elements.push(
                  <Popover content={value.popOver} trigger="click">
                    {timespan}
                  </Popover>
                );
              } else elements.push(timespan);

              startH = 0;
              startM = 0;
            }
            return elements;
          })
        )}
    </>
  );

  return (
    <>
      <Collapse
        size="small"
        items={[
          {
            key: 1,
            label: "Key",
            children: (
              <div className="amenity-schedule-key-container">
                {recurringScheduleItems?.map((value, index) => {
                  return (
                    <>
                      <Button
                        className="amenity-schedule-key-color"
                        color="primary"
                        style={{
                          backgroundColor: value.color,
                          borderColor: value.color,
                        }}
                        variant={
                          enabledRecurringKeys[index] ? "solid" : "outlined"
                        }
                        onClick={() => toggleRecurringKey(index)}
                      />
                      <div>{value.description}</div>
                    </>
                  );
                })}
                {scheduleItems?.map((value, index) => {
                  return (
                    <>
                      <Button
                        className="amenity-schedule-key-color"
                        color="primary"
                        style={{
                          backgroundColor: value.color,
                          borderColor: value.color,
                        }}
                        variant={enabledKeys[index] ? "solid" : "outlined"}
                        onClick={() => toggleKey(index)}
                      />
                      <div>{value.description}</div>
                    </>
                  );
                })}
              </div>
            ),
          },
        ]}
      />
      <br />
      <Flex
        style={{
          flexWrap: "wrap",
          gap: 10,
        }}
      >
        <Typography.Text
          style={{
            alignContent: "center",
          }}
        >
          {t("Week")}:
        </Typography.Text>
        <Flex
          style={{
            gap: 5,
          }}
        >
          <Button
            icon={<LeftOutlined />}
            onClick={() => setSchedWeek(schedWeek.subtract(1, "week"))}
          />
          <DatePicker
            picker="week"
            defaultValue={dayjs()}
            value={schedWeek}
            onChange={(value) => setSchedWeek(value ?? dayjs())}
            allowClear={false}
            renderExtraFooter={() => {
              return (
                <Flex
                  style={{
                    justifyContent: "center",
                    margin: 5,
                  }}
                >
                  <Button type="link" onClick={() => setSchedWeek(dayjs())}>
                    {t("This_week")}
                  </Button>
                </Flex>
              );
            }}
            format={(value) => {
              const weekFormat = "MM/DD";
              return `${dayjs(value)
                .startOf("week")
                .format(weekFormat)} \u2013 ${dayjs(value)
                .endOf("week")
                .format(weekFormat)}`;
            }}
          />
          <Button
            icon={<RightOutlined />}
            onClick={() => setSchedWeek(schedWeek.add(1, "week"))}
          />
        </Flex>
      </Flex>
      <div className="amenity-availability-container">
        {getTable(!landscape)}
      </div>
    </>
  );
};
